// Lighter clearance moment for the Education transmission — press-and-hold seal, no camera.
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Fingerprint } from "lucide-react";

const HOLD_MS = 1600;
const R = 54;
const CIRC = 2 * Math.PI * R;

export const EducationCheckpoint = ({ onDone }) => {
  const reduced = useReducedMotion();
  const [stage, setStage] = useState("gate"); // gate | granted
  const [progress, setProgress] = useState(0);
  const holdRef = useRef(null);
  const doneRef = useRef(false);

  const finish = () => {
    try { sessionStorage.setItem("blv_edu_clearance", "1"); } catch {}
    onDone?.();
  };

  useEffect(() => {
    if (reduced) finish();
    return () => clearInterval(holdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grant = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    clearInterval(holdRef.current);
    setProgress(100);
    setStage("granted");
    setTimeout(finish, 1900);
  };

  const startHold = () => {
    if (doneRef.current) return;
    const start = Date.now();
    clearInterval(holdRef.current);
    holdRef.current = setInterval(() => {
      const p = Math.min(100, Math.round(((Date.now() - start) / HOLD_MS) * 100));
      setProgress(p);
      if (p >= 100) grant();
    }, 40);
  };

  const endHold = () => {
    if (doneRef.current) return;
    clearInterval(holdRef.current);
    setProgress(0);
  };

  if (reduced) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center overflow-hidden bg-onyx" data-testid="education-checkpoint">
      <div className="grain-dark pointer-events-none absolute inset-0" />
      <div className="vignette pointer-events-none absolute inset-0" />
      <AnimatePresence mode="wait">
        {stage === "gate" ? (
          <motion.div key="gate" className="px-6 text-center" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-[10px] uppercase tracking-[0.5em] text-gold">
              <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold align-middle" aria-hidden="true" />
              Education network checkpoint
            </p>
            <h1 className="mt-5 font-display text-4xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-6xl">Credential<br />Verification</h1>
            <p className="mx-auto mt-4 max-w-sm font-body text-sm leading-relaxed text-silver/75">
              Press and hold the seal to authorize your institution. Purely cinematic — nothing is verified or recorded.
            </p>
            <div className="mt-9 flex justify-center">
              <button
                onPointerDown={startHold} onPointerUp={endHold} onPointerLeave={endHold} onPointerCancel={endHold}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !e.repeat) { e.preventDefault(); grant(); } }}
                aria-label="Press and hold to authorize"
                data-testid="checkpoint-seal-btn"
                className="relative flex h-36 w-36 select-none items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-gold"
                style={{ touchAction: "none", WebkitUserSelect: "none" }}
              >
                <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 120 120" aria-hidden="true">
                  <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(201,162,39,0.2)" strokeWidth="2.5" />
                  <circle cx="60" cy="60" r={R} fill="none" stroke="#E7C873" strokeWidth="2.5"
                    strokeDasharray={CIRC} strokeDashoffset={CIRC - (CIRC * progress) / 100}
                    style={{ transition: "stroke-dashoffset 60ms linear", filter: "drop-shadow(0 0 6px rgba(231,200,115,0.6))" }} />
                </svg>
                <span className={`flex h-24 w-24 items-center justify-center rounded-full border transition-colors duration-200 ${progress > 0 ? "border-gold bg-gold/10" : "border-gold/40 bg-charcoal"}`}>
                  <Fingerprint size={40} className={progress > 0 ? "text-goldlight" : "text-gold/70"} strokeWidth={1.2} />
                </span>
              </button>
            </div>
            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.3em] text-silver/50" data-testid="checkpoint-progress">
              {progress > 0 ? `Authorizing… ${progress}%` : "Hold to authorize"}
            </p>
            <button onClick={grant} data-testid="checkpoint-skip-btn"
              className="mt-7 border border-silver/40 px-7 py-3 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">
              Proceed Without Verification
            </button>
          </motion.div>
        ) : (
          <motion.div key="granted" className="px-6 text-center" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} data-testid="checkpoint-granted">
            <motion.div className="mx-auto inline-block -rotate-2 border-2 border-gold px-6 py-2.5 shadow-[0_0_40px_rgba(201,162,39,0.25)]"
              initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}>
              <span className="border border-gold/40 px-3 py-1 font-display text-3xl uppercase tracking-[0.15em] text-gold sm:text-4xl">Credentials Accepted</span>
            </motion.div>
            <motion.p className="mt-6 font-cinzel text-xs uppercase tracking-[0.35em] text-cream/90 sm:text-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
              Welcome to the Education Network
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
